import { useEffect, useState } from "react";
import axios from "axios";
import { Search, MapPin, Layers, Clock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY = { q: "", location: "all", department: "all", experience: "all" };

export default function JobSearch({ value, onChange, onSubmit, variant = "card" }) {
  const [opts, setOpts] = useState({ locations: [], departments: [], experiences: [] });
  const v = value ?? EMPTY;

  useEffect(() => {
    axios.get(`${API}/jobs/filters`).then((r) => setOpts(r.data)).catch(() => {});
  }, []);

  const set = (k, val) => onChange({ ...v, [k]: val });

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    onSubmit?.(v);
  };

  const containerCls =
    variant === "card"
      ? "bg-white rounded-2xl shadow-2xl shadow-black/10 border border-white/40 backdrop-blur-md p-5 sm:p-6"
      : "bg-slate-50 border border-slate-200 rounded-2xl p-5 sm:p-6";

  return (
    <form
      data-testid="job-search-bar"
      onSubmit={handleSubmit}
      className={`${containerCls} grid grid-cols-1 lg:grid-cols-12 gap-3 lg:gap-4 items-stretch`}
    >
      <div className="lg:col-span-4 relative">
        <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">Keyword</label>
        <Search className="w-4 h-4 absolute left-3 top-[34px] text-slate-400 pointer-events-none" />
        <Input
          data-testid="job-search-keyword"
          placeholder="Job title, skill, company…"
          value={v.q}
          onChange={(e) => set("q", e.target.value)}
          className="pl-9 h-11 bg-white"
        />
      </div>

      <div className="lg:col-span-3">
        <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">Location</label>
        <Select value={v.location} onValueChange={(val) => set("location", val)}>
          <SelectTrigger data-testid="job-search-location" className="h-11 bg-white">
            <span className="inline-flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-slate-400" /><SelectValue placeholder="All locations" /></span>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All locations</SelectItem>
            {opts.locations.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="lg:col-span-2">
        <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">Department</label>
        <Select value={v.department} onValueChange={(val) => set("department", val)}>
          <SelectTrigger data-testid="job-search-dept" className="h-11 bg-white">
            <span className="inline-flex items-center gap-2"><Layers className="w-3.5 h-3.5 text-slate-400" /><SelectValue placeholder="All" /></span>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All departments</SelectItem>
            {opts.departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="lg:col-span-2">
        <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">Experience</label>
        <Select value={v.experience} onValueChange={(val) => set("experience", val)}>
          <SelectTrigger data-testid="job-search-exp" className="h-11 bg-white">
            <span className="inline-flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-slate-400" /><SelectValue placeholder="All" /></span>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any experience</SelectItem>
            {opts.experiences.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="lg:col-span-1 flex items-end">
        <Button
          type="submit"
          data-testid="job-search-submit"
          className="w-full h-11 bg-primary hover:bg-primary-hover text-white font-semibold"
        >
          Search
        </Button>
      </div>
    </form>
  );
}
